import { Coding } from "@medplum/fhirtypes";

import { z } from "zod";

import { AllowedFileTypes, MAX_FILE_SIZE_MB } from "@/types/uploads";
import { isFileSizeAllowed } from "@/utils/uploads";
import { zCoding } from "@/utils/zod";

const DEFAULT_FILE_TYPE_ERROR_MESSAGE =
  "Only .pdf, .jpeg, .jpg, .png, .mp4, .csv, .txt, .docx, and .xlsx files are supported";

/**
 * Builds the upload form schema for one set of accepted file types.
 *
 * @param acceptedFileTypes - MIME types the file must match. Defaults to every `AllowedFileTypes` value.
 * @param fileTypeErrorMessage - Shown when the file's type is not accepted.
 * @returns The Zod schema the upload drawer validates against.
 */
export function buildFileUploadFormSchema(
  acceptedFileTypes: AllowedFileTypes[] = Object.values(AllowedFileTypes),
  fileTypeErrorMessage: string = DEFAULT_FILE_TYPE_ERROR_MESSAGE,
) {
  return z
    .object({
      fileName: z.string().min(1, "This field is required"),
      file: z
        .instanceof(File, { message: "This field is required" })
        .refine((file) => isFileSizeAllowed(file), {
          message: `File size cannot exceed ${MAX_FILE_SIZE_MB}MB`,
        })
        .refine((file) => acceptedFileTypes.includes(file.type as AllowedFileTypes), {
          message: fileTypeErrorMessage,
        }),
      fileType: zCoding(),
      description: z.string().optional(),
      visibleToClient: z.boolean(),
      fileChanged: z.boolean(),
    })
    .refine((values) => !values.fileChanged || values.file?.size > 0, {
      message: "This file is empty",
      path: ["file"],
    });
}

export const FileUploadFormSchema = buildFileUploadFormSchema();

// "file" and "fileType" should be null at first, but blocked from submitting until they're filled
export interface FileUploadFormType
  extends Omit<z.infer<typeof FileUploadFormSchema>, "file" | "fileType"> {
  file: File | null;
  fileType: Coding | null;
}

export const FILE_UPLOAD_INITIAL_VALUES: FileUploadFormType = {
  fileName: "",
  file: null,
  fileType: null,
  description: "",
  visibleToClient: false,
  fileChanged: true,
};
